import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Truck, Plus, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';

const CM_TYPES = [
  ['company_car', 'Company car / sedan'], ['suv_4wd', 'SUV / 4WD'], ['van_light', 'Light van (to 3.0t)'],
  ['dual_cab_ute', 'Dual-cab ute'], ['van_medium', 'Medium van (3.0–4.5t)'], ['light_rigid', 'Light rigid (3.5–4.5t)'],
];
const HM_TYPES = [
  ['rigid_4_5_12t', 'Rigid 4.5–12t'], ['rigid_over_12t', 'Rigid over 12t'], ['prime_mover', 'Prime mover'],
  ['semi_trailer', 'Semi-trailer'], ['dog_trailer', 'Dog trailer / dolly'], ['bus_under_25', 'Bus < 25 seats'],
  ['bus_25_plus', 'Bus 25+ seats'], ['tipper_agitator', 'Tipper / agitator'],
];
const HM_SET = new Set(HM_TYPES.map(([k]) => k));
const USES = [
  ['business_general', 'Business use, general'], ['own_goods', 'Carrying own goods'],
  ['trade_construction', 'Trade & construction'], ['courier_delivery', 'Courier / delivery'],
  ['goods_for_reward', 'Goods for reward'], ['passenger_for_reward', 'Passenger for reward'],
  ['rideshare', 'Rideshare (refer)'], ['dangerous_goods', 'Dangerous goods (refer)'],
];
const TERRITORIES = [
  ['sydney_metro', 'Sydney metro'], ['melbourne_metro', 'Melbourne metro'], ['brisbane_metro', 'Brisbane metro'],
  ['perth_metro', 'Perth metro'], ['adelaide_metro', 'Adelaide metro'], ['other_capital_metro', 'Other capital'],
  ['regional', 'Regional'], ['remote', 'Remote'],
];
const DRIVERS = [
  ['clean_25_65', '25–65, clean 5 yrs'], ['one_at_fault', 'One at-fault claim'],
  ['two_plus_at_fault', '2+ at-fault claims'], ['under_25', 'Under 25'], ['under_21', 'Under 21'],
  ['over_70', 'Over 70'], ['unlisted', 'Any driver (unlisted)'],
];
const CONTROLS = [
  ['telematics', 'Telematics recording driver behaviour', true],
  ['maintenance', 'Documented preventive maintenance (records producible)', true],
  ['dashcam', 'Forward-facing dash cameras', false],
  ['behaviour_review', 'Driver data reviewed monthly', false],
  ['gps', 'GPS tracking on all vehicles', false],
  ['driver_cam', 'Driver-facing cameras', false],
  ['prestart', 'Pre-start / daily inspections recorded', false],
  ['licence_verified', 'Licences verified before driving', false],
  ['driver_policy', 'Written driver policy (phones, fatigue, alcohol)', false],
  ['secure_compound', 'Secure overnight compound + CCTV', false],
];
const STATE_TERR = { NSW: 'sydney_metro', VIC: 'melbourne_metro', QLD: 'brisbane_metro', WA: 'perth_metro', SA: 'adelaide_metro' };

export default function VehicleScheduleField({ classId, form, setForm, homeState, onSwitchClass }) {
  const heavyClass = classId === 'heavy_motor';
  const vehicles = form.vehicles || [];
  const controls = form.risk_controls || {};
  const defTerr = STATE_TERR[homeState] || 'other_capital_metro';

  const addRow = () => setForm(p => ({
    ...p,
    vehicles: [...(p.vehicles || []), {
      rego: '', year: new Date().getFullYear(), type: heavyClass ? 'rigid_4_5_12t' : 'dual_cab_ute',
      value: '', use: heavyClass ? 'own_goods' : 'business_general', territory: defTerr, driver: 'clean_25_65',
    }],
  }));
  const setRow = (i, key, val) => setForm(p => {
    const v = [...(p.vehicles || [])];
    v[i] = { ...v[i], [key]: val };
    return { ...p, vehicles: v };
  });
  const delRow = (i) => setForm(p => ({ ...p, vehicles: (p.vehicles || []).filter((_, j) => j !== i) }));
  const toggleControl = (key) => setForm(p => ({ ...p, risk_controls: { ...(p.risk_controls || {}), [key]: !(p.risk_controls || {})[key] } }));

  const heavyCount = vehicles.filter(v => HM_SET.has(v.type)).length;
  const mismatch = !heavyClass && heavyCount > 0 ? 'heavy_motor'
    : heavyClass && vehicles.length > 0 && heavyCount === 0 ? 'commercial_motor' : null;
  const creditCount = CONTROLS.filter(([k]) => controls[k]).length;

  return (
    <Card className="p-4 rounded-sm bg-white space-y-3" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #1A4C7C' }} data-testid="vehicle-schedule-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Vehicle schedule — rated per vehicle</p>
            <p className="text-[11px]" style={{ color: '#5C6B7A' }}>Each vehicle is priced on its own value, type, use, garaged territory and worst regular driver. GVM above 4.5t rates on the Heavy Motor scale.</p>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="vehicle-add-btn"
          className="rounded-sm text-xs shrink-0" style={{ borderColor: 'rgba(26,76,124,0.35)', color: '#1A4C7C' }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add vehicle
        </Button>
      </div>

      {mismatch && (
        <button type="button" onClick={() => onSwitchClass && onSwitchClass(mismatch)} data-testid="motor-class-switch-btn"
          className="w-full text-left px-3 py-2 rounded-sm text-xs flex items-center gap-2"
          style={{ background: 'rgba(232,160,32,0.08)', border: '1px solid rgba(232,160,32,0.4)', color: '#B27516' }}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {mismatch === 'heavy_motor'
            ? `${heavyCount} vehicle(s) above 4.5t GVM — the split is a declared number, not an election. Switch to Heavy Motor.`
            : 'No vehicle above 4.5t GVM — every vehicle is on the Commercial Motor scale. Switch to Commercial Motor.'}
        </button>
      )}

      {vehicles.length === 0 && (
        <p className="text-xs py-1" style={{ color: '#5C6B7A' }} data-testid="vehicle-schedule-empty">
          No vehicles listed — the quote will fall back to fleet size × total value with default factors. Listing vehicles prices each one properly.
        </p>
      )}

      {vehicles.map((v, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-9 gap-2 items-end p-2 rounded-sm" style={{ background: 'rgba(26,76,124,0.03)' }} data-testid={`vehicle-row-${i}`}>
          <div>
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Rego</p>
            <Input value={v.rego || ''} onChange={e => setRow(i, 'rego', e.target.value)} className="h-8 rounded-sm text-xs" data-testid={`vehicle-rego-${i}`} />
          </div>
          <div>
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Year</p>
            <Input type="number" value={v.year ?? ''} onChange={e => setRow(i, 'year', parseInt(e.target.value) || '')} className="h-8 rounded-sm text-xs" data-testid={`vehicle-year-${i}`} />
          </div>
          <div className="col-span-2 md:col-span-1">
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Type</p>
            <select value={v.type || ''} onChange={e => setRow(i, 'type', e.target.value)} className="w-full h-8 px-1 border border-input rounded-sm text-xs bg-white" data-testid={`vehicle-type-${i}`}>
              <optgroup label="Commercial Motor (≤4.5t)">{CM_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</optgroup>
              <optgroup label="Heavy Motor (>4.5t)">{HM_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</optgroup>
            </select>
          </div>
          <div>
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Value ($)</p>
            <Input type="number" value={v.value ?? ''} onChange={e => setRow(i, 'value', parseFloat(e.target.value) || '')} className="h-8 rounded-sm text-xs" data-testid={`vehicle-value-${i}`} />
          </div>
          <div>
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Use</p>
            <select value={v.use || ''} onChange={e => setRow(i, 'use', e.target.value)} className="w-full h-8 px-1 border border-input rounded-sm text-xs bg-white" data-testid={`vehicle-use-${i}`}>
              {USES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Garaged territory</p>
            <select value={v.territory || ''} onChange={e => setRow(i, 'territory', e.target.value)} className="w-full h-8 px-1 border border-input rounded-sm text-xs bg-white" data-testid={`vehicle-territory-${i}`}>
              {TERRITORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-col items-center justify-end pb-1">
            <p className="text-[10px]" style={{ color: v.registered === false ? '#E24B4A' : '#5C6B7A' }}>Reg/RWC</p>
            <input type="checkbox" checked={v.registered !== false} onChange={e => setRow(i, 'registered', e.target.checked)}
              className="mt-1.5 accent-[#1A4C7C]" data-testid={`vehicle-registered-${i}`} />
          </div>
          <div className="flex items-end gap-1.5">
            <div className="flex-1">
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Worst driver</p>
              <select value={v.driver || ''} onChange={e => setRow(i, 'driver', e.target.value)} className="w-full h-8 px-1 border border-input rounded-sm text-xs bg-white" data-testid={`vehicle-driver-${i}`}>
                {DRIVERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => delRow(i)} className="h-8 px-1.5" data-testid={`vehicle-remove-${i}`}>
              <Trash2 className="w-3.5 h-3.5" style={{ color: '#E24B4A' }} />
            </button>
          </div>
        </div>
      ))}

      {(() => {
        const unreg = vehicles.filter(v => v.registered === false).length;
        return unreg > 0 && (
          <div className="px-3 py-2 rounded-sm text-xs flex items-center gap-2" data-testid="unregistered-hold-note"
            style={{ background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.3)', color: '#B03230' }}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {unreg} vehicle{unreg > 1 ? 's' : ''} declared unregistered or unroadworthy — those items are HELD at bind until an underwriter resolves them (Section 5 exclusion (f)). The rest of the fleet binds normally.
          </div>
        );
      })()}

      <div className="pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
        <div className="flex items-center gap-2 mb-1.5">
          <ShieldCheck className="w-4 h-4" style={{ color: '#1D9E75' }} />
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Risk management credits — the largest lever in this class</p>
          {creditCount > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(29,158,117,0.12)', color: '#1D9E75' }} data-testid="risk-credit-count">{creditCount} control{creditCount > 1 ? 's' : ''} evidenced</span>}
        </div>
        <p className="text-[11px] mb-2" style={{ color: '#5C6B7A' }}>Telematics alone is a 12% credit; the evidenced stack caps at 35%. A fleet that cannot produce maintenance records after a serious accident faces litigation costs 4.2× higher — the record matters more than the discount.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          {CONTROLS.map(([k, label, major]) => (
            <label key={k} className="flex items-center gap-2 text-xs cursor-pointer py-0.5" data-testid={`risk-control-${k}`}>
              <input type="checkbox" checked={!!controls[k]} onChange={() => toggleControl(k)} className="accent-[#1A4C7C]" />
              <span style={{ color: '#0D1B2A', fontWeight: major ? 600 : 400 }}>{label}{major ? ' — major credit' : ''}</span>
            </label>
          ))}
        </div>
      </div>
    </Card>
  );
}
